import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resolveApiBaseUrl } from '@/lib/apiBaseUrl';
import { createErrorReportClient, type ErrorReportClient } from '@/lib/errorReportClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

interface Props {
  children: ReactNode;
  /**
   * Injected reporter (tests / Storybook). When omitted, the boundary builds a
   * default client targeting the same baseUrl as the auth adapter.
   */
  reporter?: ErrorReportClient;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * 全局错误边界。
 * - 捕获 React 渲染期错误，避免空白页
 * - 在 DEV 下展示堆栈，PROD 下仅展示通用提示
 * - 渲染期错误会脱敏后通过 `/api/v1/error-reports` 写入审计；上报失败不会再次
 *   抛出，避免错误边界自身陷入循环
 */
export default class ErrorBoundary extends Component<Props, State> {
  private readonly reporter: ErrorReportClient;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.reporter =
      props.reporter ??
      createErrorReportClient({
        baseUrl: resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL),
        // ErrorBoundary 渲染在 AuthProvider 之上，无法订阅 React context；改为
        // 直接读取 `tokenStorage`（与 createApiClientAuthAdapter 共享同一存储），
        // 确保 `/api/v1/error-reports` 在已登录态下携带 `Authorization: Bearer`，
        // 避免被全局 JwtAuthGuard 拒为 401。Issue #178 / PR #184 Codex review。
        getAccessToken: getStoredAccessToken,
      });
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    const componentName = extractTopComponent(errorInfo.componentStack ?? null) ?? undefined;
    const route = typeof window === 'undefined' ? undefined : window.location.pathname;
    const releaseId = import.meta.env.VITE_RELEASE_ID;
    void this.reporter.report({
      error,
      ...(componentName ? { componentName } : {}),
      ...(route ? { route } : {}),
      ...(typeof releaseId === 'string' && releaseId.length > 0 ? { releaseId } : {}),
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-lg space-y-6 rounded-lg border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <h1 className="text-xl font-semibold">页面发生异常</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            页面渲染过程中出现了未处理的错误。请刷新页面重试，若问题持续出现请联系系统管理员。
          </p>
          {import.meta.env.DEV && this.state.error ? (
            <pre className="max-h-64 overflow-auto rounded bg-muted p-3 text-xs text-muted-foreground">
              {this.state.error.stack ?? this.state.error.message}
            </pre>
          ) : null}
          <div className="flex gap-2">
            <Button onClick={this.handleReload} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              刷新页面
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * React's componentStack is a newline-separated list like:
 *   "    in CrashChild\n    in ErrorBoundary"
 * We only emit the top-most caller (the component that actually threw), and
 * cap the value so it cannot blow past the backend column limit. The result
 * carries NO PII — it's a developer-facing identifier.
 */
function extractTopComponent(componentStack: string | null): string | null {
  if (!componentStack) return null;
  const first = componentStack
    .split('\n')
    .map((s) => s.trim())
    .find((s) => s.startsWith('in '));
  if (!first) return null;
  const name = first.slice(3).split(/\s/)[0];
  if (!name) return null;
  return name.slice(0, 80);
}
